import Vue from 'vue'
const state = {
  conversations: [],
  activeConversation: {
    isBuddyWriting: false
  },
  activeConversationID: 0,
  clearedConversations: [],
  writingParticipant: null,
  messageToForward: {},
  isFirstLoad: true,
  conversationPage: 1,
  messagePerConversation: 15,
  userSettings: undefined
}

const incrementCount = (counts, key) => {
  counts.set(key, (counts.get(key) || 0) + 1)
}

const messageDateKey = message => message.CreateDate.substring(0, 10)

const dateInfoMessage = (conversation, message) => {
  const conversationID = conversation.GroupID || conversation.ChannelID || conversation.ConversationID
  const dateKey = messageDateKey(message)
  return {
    MessageID: `date-${conversationID}-${dateKey}`,
    MessageType: 99,
    CreateDate: message.CreateDate
  }
}

const sortMessages = messages => {
  return messages
    .map((message, index) => ({ message, index }))
    .sort((a, b) => {
      const dateDifference = new Date(a.message.CreateDate) - new Date(b.message.CreateDate)
      if (dateDifference !== 0) {
        return dateDifference
      }

      if (
        Number.isInteger(a.message.MessageIDint) &&
        Number.isInteger(b.message.MessageIDint) &&
        a.message.MessageIDint !== b.message.MessageIDint
      ) {
        return a.message.MessageIDint - b.message.MessageIDint
      }

      return a.index - b.index
    })
    .map(entry => entry.message)
}

const getters = {
  sortedConversations: (state, getters, rootState) => {
    let convs = state.conversations
    let groups = rootState.group.groups
    let channels = rootState.channel.channels
    for (let group of groups) {
      group.ConversationID = group.GroupID
      group.isGroupConversation = true
      group.isBuddyWriting = false
    }
    for (let channel of channels) {
      channel.ConversationID = channel.ChannelID
      channel.isChannelConversation = true
      channel.isBuddyWriting = false
    }
    let merged = convs.concat(groups).concat(channels)

    const latestDirectMessages = new Map()
    const latestSharedMessages = new Map()
    for (const message of rootState.message.messages) {
      const createDate = new Date(message.CreateDate).getTime()

      const previousDirectMessage = latestDirectMessages.get(message.ConversationID)
      if (previousDirectMessage === undefined || createDate >= previousDirectMessage.createDate) {
        latestDirectMessages.set(message.ConversationID, {
          createDate,
          message
        })
      }

      const previousSharedMessage = latestSharedMessages.get(message.To)
      if (previousSharedMessage === undefined || createDate >= previousSharedMessage.createDate) {
        latestSharedMessages.set(message.To, {
          createDate,
          message
        })
      }
    }

    const lastMessage = conversation => {
      const messageEntry = conversation.GroupID || conversation.ChannelID
        ? latestSharedMessages.get(conversation.GroupID || conversation.ChannelID)
        : latestDirectMessages.get(conversation.ConversationID)

      return messageEntry === undefined ? conversation.LastMessage : messageEntry.message
    }

    merged.sort(function (a, b) {
      let aLast = lastMessage(a)
      let bLast = lastMessage(b)
      if (aLast === undefined || bLast === undefined) {
        return -1
      }
      var aDate = new Date(aLast.UpdateDate === undefined ? aLast.CreateDate : aLast.UpdateDate)
      var bDate = new Date(bLast.UpdateDate === undefined ? bLast.CreateDate : bLast.UpdateDate)
      return bDate - aDate
    })

    const conversationIds = new Set()
    let mergedNoDuplicate = merged.filter(obj => {
      if (conversationIds.has(obj.ConversationID)) {
        return false
      }
      conversationIds.add(obj.ConversationID)
      return true
    })
    return mergedNoDuplicate
  },

  sortedMessagesOfConv: (state, getters, rootState) => conversation => {
    let messages
    if (conversation.GroupID || conversation.ChannelID) {
      messages = rootState.message.messages.filter(message => {
        return message.To === (conversation.GroupID || conversation.ChannelID)
      })
    } else {
      messages = rootState.message.messages.filter(message => {
        return message.ConversationID === conversation.ConversationID
      })
    }

    messages = sortMessages(messages)

    if (conversation.ConversationID === state.activeConversation.ConversationID) {
      let previousMessage
      const viewMessages = []

      for (const message of messages) {
        const startsNewDay = previousMessage &&
          messageDateKey(previousMessage) !== messageDateKey(message)
        const continueFromSameBuddy = Boolean(
          previousMessage &&
          !startsNewDay &&
          previousMessage.From === message.From &&
          previousMessage.MessageType !== 7
        )

        if (!previousMessage || startsNewDay) {
          viewMessages.push(dateInfoMessage(conversation, message))
        }

        viewMessages.push({
          ...message,
          continueFromSameBuddy
        })
        previousMessage = message
      }

      return viewMessages
    } else {
      return messages
    }
  },

  lastMessageOfConversation: (state, getters, rootState) => conversation => {
    const uniqueId = conversation.GroupID || conversation.ChannelID
    let lmsg
    let lastCreateDate = -Infinity

    for (const message of rootState.message.messages) {
      const belongsToConversation = uniqueId
        ? message.To === uniqueId
        : message.ConversationID === conversation.ConversationID

      if (belongsToConversation) {
        const createDate = new Date(message.CreateDate).getTime()
        if (createDate >= lastCreateDate) {
          lastCreateDate = createDate
          lmsg = message
        }
      }
    }

    lmsg = lmsg === undefined ? conversation.LastMessage : lmsg
    if (lmsg !== undefined && lmsg.UpdateDate === undefined) {
      lmsg.UpdateDate = lmsg.CreateDate
    }
    return lmsg
  },

  unreadMessagesCountOfConversation: (state, getters, rootState) => conversation => {
    if (conversation === undefined) {
      return 0
    }

    let unreadMessageCount = 0
    if (conversation.GroupID || conversation.ChannelID) {
      const uniqueId = conversation.GroupID || conversation.ChannelID
      for (const msg of rootState.message.messages) {
        if (
          msg.To === uniqueId &&
          msg.Status < 3 &&
          msg.From !== rootState.session.user.UserID
        ) {
          unreadMessageCount++
        }
      }
    } else {
      for (const msg of rootState.message.messages) {
        if (
          msg.ConversationID === conversation.ConversationID &&
          msg.Status < 3 &&
          msg.From !== rootState.session.user.UserID &&
          msg.MessageType !== 7
        ) {
          unreadMessageCount++
        }
      }
    }
    return unreadMessageCount
  },
  unreadMessagesCountOfConversationTotal: (state, getters, rootState) => {
    const directCounts = new Map()
    const sharedCounts = new Map()
    const userId = rootState.session.user.UserID

    for (const message of rootState.message.messages) {
      if (
        message.Status < 3 &&
        message.From !== userId &&
        message.MessageType !== 7
      ) {
        incrementCount(directCounts, message.ConversationID)
      }
    }

    for (const messageStatus of rootState.messagestatus.messageStatuses) {
      if (
        messageStatus.UserID === userId &&
        messageStatus.Status < 3
      ) {
        incrementCount(sharedCounts, messageStatus.UniqueID)
      }
    }

    var totalCount = 0
    for (const conversation of getters.sortedConversations) {
      if (conversation.isGroupConversation || conversation.isChannelConversation) {
        totalCount += sharedCounts.get(conversation.ConversationID) || 0
      } else {
        totalCount += directCounts.get(conversation.ConversationID) || 0
      }
    }
    return totalCount
  }
}

const mutations = {
  setClearedConversations (state, payload) {
    state.clearedConversations = payload
  },
  setConversations (state, data) {
    for (let conv of data) {
      let newMsg = JSON.parse(JSON.stringify(data.rootState.message.newMessageModel))
      Vue.set(conv, 'newMessage', newMsg)

      let uploaders = JSON.parse(JSON.stringify(data.rootState.message.uploadersModel))
      Vue.set(conv, 'uploaders', uploaders)

      let preview = JSON.parse(JSON.stringify(data.rootState.message.previewModel))
      Vue.set(conv, 'preview', preview)
    }

    var dataLast = JSON.parse(JSON.stringify(data))
    // console.log(dataLast)
    for (const iterator of state.clearedConversations) {
      if (iterator.isRemove === true) {
        let item
        if (data.isGroup) {
          item = dataLast.find(x => x.GroupID === iterator.UniqueID && new Date(x.CreateDate).getTime() <= new Date(iterator.ClearDate).getTime())
        } else {
          item = dataLast.find(x => x.ConversationID === iterator.UniqueID && new Date(x.CreateDate).getTime() <= new Date(iterator.ClearDate).getTime())
        }

        let i = dataLast.indexOf(item)
        if (i !== -1) {
          let filteredMessages = data.rootState.message.messages.filter(x => x.ConversationID === iterator.UniqueID)
          if (filteredMessages.length === 0) {
            dataLast.splice(i, 1)
          }
        }
      } else {
        let item2
        if (iterator.isGroup) { // aslında is Group kontrolü fazlalık, bunu group.js te kontrol ediyoruz.
          item2 = dataLast.find(x => x.GroupID === iterator.UniqueID && new Date(x.CreateDate).getTime() <= new Date(iterator.ClearDate).getTime())
        } else {
          item2 = dataLast.find(x => x.ConversationID === iterator.UniqueID && new Date(x.CreateDate).getTime() <= new Date(iterator.ClearDate).getTime())
        }

        if (item2) {
          item2.LastMessage = {}
        }
      }
    }
    // console.log(dataLast)
    state.conversations = dataLast
    let activeConversationID = state.activeConversation && state.activeConversation.ConversationID
    if (activeConversationID) {
      let activeConversation = state.conversations.find(x => x.ConversationID === activeConversationID)
      if (activeConversation) {
        state.activeConversation = activeConversation
      }
    }
  },

  setActiveConversation (state, data) {
    state.activeConversation = data
  },

  addNewConversation (state, data) {
    let newMsg = JSON.parse(JSON.stringify(data.rootState.message.newMessageModel))
    Vue.set(data, 'newMessage', newMsg)

    let uploaders = JSON.parse(JSON.stringify(data.rootState.message.uploadersModel))
    Vue.set(data, 'uploaders', uploaders)

    let preview = JSON.parse(JSON.stringify(data.rootState.message.previewModel))
    Vue.set(data, 'preview', preview)
    delete data['rootState'] //circular object reference error için eklendi.
    state.conversations.push(data)
  },

  setConversationContactWriting (state, data) {
    var conv = getters.sortedConversations(state, getters, data.rootState).find(conv => conv.ConversationID === data.id)
    if (conv && conv.isChannelConversation) {
      Vue.set(conv, 'writingParticipant', null)
      return
    }
    if (data.isGroup) {
      var writingParticipant = data.state ? data.from : null
      Vue.set(conv, 'writingParticipant', writingParticipant)
      // conv.writingParticipant = data.state ? data.from : null
      state.writingParticipant = writingParticipant
    } else {
      if (conv) {
        conv.isBuddyWriting = data.state
      }
    }
  },

  setActiveConversationImageUploaderFiles (state, data) {
    if (process.env.VUE_APP_CHAT_ATTACHMENT_UPLOAD_ENABLED === 'false') {
      state.activeConversation.uploaders.imageUploader.files = []
      return
    }
    state.activeConversation.uploaders.imageUploader.files = data
  },

  setActiveConversationDocumentUploaderFiles (state, data) {
    if (process.env.VUE_APP_CHAT_ATTACHMENT_UPLOAD_ENABLED === 'false') {
      state.activeConversation.uploaders.documentUploader.files = []
      return
    }
    state.activeConversation.uploaders.documentUploader.files = data
  },

  appendActiveConversationImageUploaderFiles (state, data) {
    if (process.env.VUE_APP_CHAT_ATTACHMENT_UPLOAD_ENABLED === 'false') {
      return
    }
    state.activeConversation.uploaders.imageUploader.files = state.activeConversation.uploaders.imageUploader.files.concat(data)
  },

  appendActiveConversationDocumentUploaderFiles (state, data) {
    if (process.env.VUE_APP_CHAT_ATTACHMENT_UPLOAD_ENABLED === 'false') {
      return
    }
    state.activeConversation.uploaders.documentUploader.files = state.activeConversation.uploaders.documentUploader.files.concat(data)
  },

  setActiveConversationImageUploaderVisibility (state, data) {
    if (process.env.VUE_APP_CHAT_ATTACHMENT_UPLOAD_ENABLED === 'false') {
      state.activeConversation.uploaders.imageUploader.isVisible = false
      return
    }
    state.activeConversation.uploaders.imageUploader.isVisible = data
  },

  setActiveConversationDocumentUploaderVisibility (state, data) {
    if (process.env.VUE_APP_CHAT_ATTACHMENT_UPLOAD_ENABLED === 'false') {
      state.activeConversation.uploaders.documentUploader.isVisible = false
      return
    }
    state.activeConversation.uploaders.documentUploader.isVisible = data
  },

  setMessageToForward (state, data) {
    state.messageToForward = data
  },

  incrementConversationPage (state) {
    state.conversationPage++
  },

  resetConversationPage (state) {
    state.conversationPage = 1
  }
}

const actions = {
  setClearedConversations (context, data) {
    data.rootState = context.rootState
    context.commit('setClearedConversations', data)
  },
  setConversations (context, data) {
    data.rootState = context.rootState
    context.commit('setConversations', data)
  },

  addNewConversation (context, data) {
    data.rootState = context.rootState
    context.commit('addNewConversation', data)
  },

  setConversationContactWriting (context, data) {
    data.rootState = context.rootState
    context.commit('setConversationContactWriting', data)
  }
}
export default {
  state,
  getters,
  mutations,
  actions
}
